/**
 * CookieConsentContext — GDPR/CCPA-compliant consent state management.
 *
 * Storage: localStorage key `abg_cookie_consent` (survives across sessions).
 * The banner is shown again when ANY of the following holds:
 *   1. No consent has ever been recorded.
 *   2. `consentDate` is older than the admin-configured `cookie_expiration_days`.
 *   3. `consentVersion` doesn't match the admin's current `cookie_policy_version`.
 *
 * Extensibility: adding a new category = one line in `DEFAULT_CATEGORIES` +
 * one entry in the preference-center list. No other code changes needed.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useSettings } from "@/contexts/SettingsContext";

const STORAGE_KEY = "abg_cookie_consent";

// Category definitions — add a new key here to introduce a new category site-wide.
// `essential` is always granted and cannot be disabled by the user.
export const DEFAULT_CATEGORIES = {
  essential: true,
  analytics: false,
  marketing: false,
  functional: false,
};

const CookieConsentContext = createContext(null);

function readStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch (err) {
    // Corrupt payload or storage disabled — fall back to "no consent".
    console.warn("[CookieConsent] readStored failed:", err);
    return null;
  }
}

function writeStored(value) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(value)); }
  catch (err) {
    // Private mode / storage disabled — non-fatal.
    console.warn("[CookieConsent] writeStored failed:", err);
  }
}

function isExpired(consentDateIso, expirationDays) {
  if (!consentDateIso) return true;
  const consented = new Date(consentDateIso).getTime();
  if (Number.isNaN(consented)) return true;
  const days = Math.max(1, Number(expirationDays) || 365);
  return Date.now() - consented > days * 24 * 60 * 60 * 1000;
}

export function CookieConsentProvider({ children }) {
  const s = useSettings();
  const currentVersion = String(s?.cookie_policy_version ?? "1.0");
  const expirationDays = Number(s?.cookie_expiration_days ?? 365);
  const enabled = s?.cookie_consent_enabled !== false; // default ON

  const [state, setState] = useState(() => readStored());
  const [preferenceOpen, setPreferenceOpen] = useState(false);

  // If admin bumped policy version OR consent is expired, wipe stale state
  // so the banner re-shows on next render.
  useEffect(() => {
    if (!state) return;
    const versionStale = String(state.consentVersion || "") !== currentVersion;
    if (versionStale || isExpired(state.consentDate, expirationDays)) {
      setState(null);
      try { localStorage.removeItem(STORAGE_KEY); }
      catch (err) { console.warn("[CookieConsent] removeItem failed:", err); }
    }
  }, [state, currentVersion, expirationDays]);

  const needsConsent = enabled && !state;

  const save = useCallback((prefs) => {
    const record = {
      ...DEFAULT_CATEGORIES,
      ...prefs,
      essential: true, // never allow disabling essentials
      consentDate: new Date().toISOString(),
      consentVersion: currentVersion,
    };
    writeStored(record);
    setState(record);
    // Broadcast for any component that wants to react (e.g. script loader).
    window.dispatchEvent(new CustomEvent("abg:cookie-consent-updated", { detail: record }));
  }, [currentVersion]);

  const acceptAll = useCallback(() => {
    save({ essential: true, analytics: true, marketing: true, functional: true });
    setPreferenceOpen(false);
  }, [save]);

  const declineNonEssential = useCallback(() => {
    save({ essential: true, analytics: false, marketing: false, functional: false });
    setPreferenceOpen(false);
  }, [save]);

  const savePreferences = useCallback((prefs) => {
    save(prefs);
    setPreferenceOpen(false);
  }, [save]);

  const revoke = useCallback(() => {
    try { localStorage.removeItem(STORAGE_KEY); }
    catch (err) { console.warn("[CookieConsent] revoke removeItem failed:", err); }
    setState(null);
    window.dispatchEvent(new CustomEvent("abg:cookie-consent-updated", { detail: null }));
  }, []);

  const has = useCallback((category) => !!state?.[category], [state]);

  const value = useMemo(() => ({
    enabled,
    needsConsent,
    preferences: state,
    has,
    acceptAll,
    declineNonEssential,
    savePreferences,
    revoke,
    preferenceOpen,
    openPreferences: () => setPreferenceOpen(true),
    closePreferences: () => setPreferenceOpen(false),
    // Admin-facing metadata — the banner reads text/colors from settings directly
    settings: s || {},
  }), [enabled, needsConsent, state, has, acceptAll, declineNonEssential, savePreferences, revoke, preferenceOpen, s]);

  return <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>;
}

export function useCookieConsent() {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) throw new Error("useCookieConsent must be used within CookieConsentProvider");
  return ctx;
}